import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';

import { IconsModule } from '../../icons/icons.module';

export interface TreeNode {
  name: string;
  children?: TreeNode[];
  id: string;
}

@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.css'],
  standalone: true,
  imports: [CommonModule, MatTreeModule, MatIconModule, IconsModule],
})
export class TreeComponent implements OnInit {
  private _data: TreeNode[] = [];

  private _initialSelection: string[] = [];

  private _initialSelectionHasBeenSet = false;

  @Input() set data(value: TreeNode[]) {
    // Save expanded state and current selection before updating
    const expandedNodeIds = this.getExpandedNodeIds();
    const currentSelection = new Set(this.selectedNodes);

    this._data = value;
    this.setupTree();

    // Restore expanded state after update
    this.restoreExpandedState(expandedNodeIds);

    // Re-apply selection state (source of truth is initialSelection)
    // Only apply if initialSelection has been explicitly set (to avoid clearing during data updates)
    // Otherwise, preserve current selection if data is being updated
    if (this._initialSelectionHasBeenSet) {
      this.applyInitialSelection(false); // Don't emit during data update to avoid reset loops
    } else if (currentSelection.size > 0 && value && value.length > 0) {
      // If initialSelection hasn't been set yet but we have current selection, preserve it
      // This handles cases where data is updated but selection should persist (e.g., during import)
      this.selectedNodes = currentSelection;
    }
  }

  get data(): TreeNode[] {
    return this._data;
  }

  selectedElement: TreeNode;

  @Input() getNodeName?: (node: TreeNode) => string;

  @Input() clickable = true;

  @Input() autoExpand = false;

  @Input() collapsable = true;

  @Input() selectable = false;

  @Input() set initialSelection(value: string[]) {
    this._initialSelection = value ?? [];
    this._initialSelectionHasBeenSet = true; // Mark that initialSelection has been explicitly set
    // When parent updates selection, reflect it immediately in the tree UI
    this.applyInitialSelection(false);
  }

  get initialSelection(): string[] {
    return this._initialSelection;
  }

  @Output() nodeClicked = new EventEmitter<TreeNode>();

  @Output() selectionChanged = new EventEmitter<TreeNode[]>();

  selectedNodes: Set<string> = new Set<string>();

  dataSource: MatTreeNestedDataSource<TreeNode>;

  treeControl: NestedTreeControl<TreeNode>;

  ngOnInit(): void {
    this.setupTree();
    // Initialize selection from initialSelection input
    this.applyInitialSelection(false);
  }

  /**
   * Applies the current initialSelection to internal selection state.
   * This REPLACES (not merges) current selection so deselections are reflected in UI.
   */
  private applyInitialSelection(emit: boolean): void {
    // Always replace selection with initialSelection (even if empty, to support clearing)
    // This ensures the tree UI reflects the parent's selection state
    this.selectedNodes = new Set<string>(this._initialSelection ?? []);
    if (emit) {
      this.emitSelectionChanged();
    }
  }

  setupTree() {
    this.dataSource = new MatTreeNestedDataSource<TreeNode>();
    this.treeControl = new NestedTreeControl<TreeNode>(
      (node: TreeNode) => node.children
    );
    this.treeControl.dataNodes = this.data;
    this.dataSource.data = this.data;
    if ((this.autoExpand || !this.collapsable) && this.treeControl.dataNodes)
      this.treeControl.expandAll();
  }

  /**
   * Collects all currently expanded node IDs recursively
   */
  private getExpandedNodeIds(): Set<string> {
    const expandedIds = new Set<string>();

    if (!this.treeControl || !this.treeControl.dataNodes) {
      return expandedIds;
    }

    const collectExpanded = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (this.treeControl.isExpanded(node)) {
          expandedIds.add(node.id);
        }
        if (node.children && node.children.length > 0) {
          collectExpanded(node.children);
        }
      }
    };

    collectExpanded(this.treeControl.dataNodes);
    return expandedIds;
  }

  /**
   * Restores the expanded state for nodes that still exist in the new data
   */
  private restoreExpandedState(expandedNodeIds: Set<string>): void {
    if (!this.treeControl || !this.treeControl.dataNodes) {
      return;
    }

    // If collapsable is false, expand all nodes
    if (!this.collapsable) {
      this.treeControl.expandAll();
      return;
    }

    // Otherwise, restore the previous expanded state
    if (expandedNodeIds.size === 0) {
      return;
    }

    const findAndExpand = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (expandedNodeIds.has(node.id)) {
          this.treeControl.expand(node);
        }
        if (node.children && node.children.length > 0) {
          findAndExpand(node.children);
        }
      }
    };

    findAndExpand(this.treeControl.dataNodes);
  }

  hasChildren = (index: number, node: TreeNode) =>
    node.children && node.children.length > 0;

  selectElement(node: TreeNode) {
    this.nodeClicked.emit(node);
    this.selectedElement = node;
  }

  toggleNodeSelection(node: TreeNode, event: Event) {
    // event.stopPropagation();
    if (this.selectedNodes.has(node.id)) {
      this.selectedNodes.delete(node.id);
      // Unselect all child nodes
      this.unselectChildNodes(node);
    } else {
      this.selectedNodes.add(node.id);
      // Select all parent nodes
      this.selectParentNodes(node);
    }
    this.emitSelectionChanged();
  }

  /**
   * Finds and selects all parent nodes of the given node
   */
  private selectParentNodes(node: TreeNode): void {
    const parentPath = this.findNodePath(node);
    // Select all nodes in the path (excluding the node itself, which is already selected)
    parentPath.forEach((parentNode) => {
      if (parentNode.id !== node.id) {
        this.selectedNodes.add(parentNode.id);
      }
    });
  }

  /**
   * Finds and unselects all descendant nodes (children, grandchildren, etc.) of the given node
   */
  private unselectChildNodes(node: TreeNode): void {
    const descendantIds = this.getAllDescendantIds(node);
    descendantIds.forEach((id) => {
      this.selectedNodes.delete(id);
    });
  }

  /**
   * Recursively collects all descendant node IDs (children, grandchildren, etc.)
   * Returns an array of all descendant node IDs
   */
  private getAllDescendantIds(node: TreeNode): string[] {
    const descendantIds: string[] = [];

    const collectDescendants = (currentNode: TreeNode) => {
      if (currentNode.children && currentNode.children.length > 0) {
        currentNode.children.forEach((child) => {
          descendantIds.push(child.id);
          collectDescendants(child);
        });
      }
    };

    collectDescendants(node);
    return descendantIds;
  }

  /**
   * Finds the path from root to the given node
   * Returns an array of nodes from root to the target node (inclusive)
   */
  private findNodePath(targetNode: TreeNode): TreeNode[] {
    const path: TreeNode[] = [];

    const findPath = (nodes: TreeNode[], currentPath: TreeNode[]): boolean => {
      for (const node of nodes) {
        const newPath = [...currentPath, node];

        if (node.id === targetNode.id) {
          path.push(...newPath);
          return true;
        }

        if (node.children && node.children.length > 0) {
          if (findPath(node.children, newPath)) {
            return true;
          }
        }
      }
      return false;
    };

    if (this.data) {
      findPath(this.data, []);
    }

    return path;
  }

  isNodeSelected(node: TreeNode): boolean {
    return this.selectedNodes.has(node.id);
  }

  private emitSelectionChanged() {
    const selectedNodesArray: TreeNode[] = [];
    const findSelectedNodes = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        if (this.selectedNodes.has(node.id)) {
          selectedNodesArray.push(node);
        }
        if (node.children && node.children.length > 0) {
          findSelectedNodes(node.children);
        }
      }
    };
    if (this.data) {
      findSelectedNodes(this.data);
    }
    this.selectionChanged.emit(selectedNodesArray);
  }

  toggleNodeExpansion(node: TreeNode, event: Event) {
    if (!this.collapsable) {
      // event.preventDefault();
      // event.stopPropagation();
      return;
    }
    this.treeControl.toggle(node);
  }

  handleNodeClick(node: TreeNode, event: Event) {
    this.selectElement(node);
    this.toggleNodeExpansion(node, event);
  }
}
